import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Mail, Lock, Eye, EyeOff, ArrowRight, Loader2, User, Users, Shield, CheckCircle2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useStatus } from '../../contexts/StatusContext';
import { Make_Authorized_API_Call, API_ENDPOINTS } from '../../api/apiClient';

type AuthMode = 'signin' | 'signup';

interface ModernLoginProps {
  onSignUpClick?: () => void;
  initialMode?: AuthMode;
}

const ModernLogin: React.FC<ModernLoginProps> = ({ onSignUpClick, initialMode = 'signin' }) => {
  const [authMode, setAuthMode] = useState<AuthMode>(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [pendingUsername, setPendingUsername] = useState('');
  const [pendingPassword, setPendingPassword] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    familyName: '',
    agreeToTerms: false
  });

  const { authenticate, completeNewPassword, needsNewPassword, register, confirmRegistration, resendVerificationCode, Is_Loading, Auth_Status, Error_Message, family_id } = useStatus();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  // CRITICAL: Watch Auth_Status and route automatically
  useEffect(() => {
    console.log('[ModernLogin] Auth_Status changed to:', Auth_Status);
    if (Auth_Status) {
      const redirectUrl = sessionStorage.getItem('postLoginRedirect');
      if (redirectUrl) {
        sessionStorage.removeItem('postLoginRedirect');
        console.log('[ModernLogin] Redirecting to saved URL:', redirectUrl);
        navigate(redirectUrl, { replace: true });
      } else {
        if (!family_id) {
          console.log('[ModernLogin] No family_id - user needs family setup');
        }
        console.log('[ModernLogin] Navigating to /tasks');
        navigate('/tasks', { replace: true });
      }
    }
  }, [Auth_Status, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (Is_Loading) return;

    if (authMode === 'signin') {
      // Sign In Flow
      console.log('[ModernLogin] Form submitted, calling authenticate...');
      try {
        await authenticate(formData.email, formData.password);
        console.log('[ModernLogin] authenticate() completed successfully');
        // Navigation happens automatically via useEffect
      } catch (err: any) {
        console.error('[ModernLogin] Login failed:', err);

        // If user hasn't verified email, show verification modal
        if (err.needsVerification) {
          console.log('[ModernLogin] User needs email verification, showing modal');

          // Cognito pool uses email as alias — confirmSignUp and resendConfirmationCode
          // both resolve the email alias to the actual username internally
          setPendingEmail(formData.email);
          setPendingUsername(formData.email);
          setPendingPassword(formData.password);
          setShowVerificationModal(true);
          toast.error('Please verify your email first. Check your inbox for the verification code.');
        }
      }
    } else {
      // Sign Up Flow
      console.log('[ModernLogin] Sign up form submitted...');

      // Validate terms agreement
      if (!formData.agreeToTerms) {
        toast.error('You must agree to the Terms of Service and Privacy Policy');
        return;
      }

      // Validate password match
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }

      // Validate password strength
      if (formData.password.length < 8) {
        toast.error('Password must be at least 8 characters');
        return;
      }

      const hasUpperCase = /[A-Z]/.test(formData.password);
      const hasLowerCase = /[a-z]/.test(formData.password);
      const hasNumber = /[0-9]/.test(formData.password);
      const hasSpecialChar = /[^A-Za-z0-9]/.test(formData.password);

      if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
        toast.error('Password must contain uppercase, lowercase, number, and special character');
        return;
      }

      try {
        // Get birth date and age from session storage (set by age verification)
        const birthDate = sessionStorage.getItem('user_birth_date');
        const ageStr = sessionStorage.getItem('user_age');
        const age = ageStr ? parseInt(ageStr) : null;

        console.log('[ModernLogin] Registration with age data:', { birthDate, age });

        const result = await register(
          formData.email,
          formData.password,
          formData.name,
          formData.familyName || undefined,
          birthDate || undefined
        );

        if (result.needsVerification) {
          console.log('[ModernLogin] Registration successful, showing verification modal');
          setPendingEmail(formData.email);
          setPendingUsername(result.username);
          setPendingPassword(formData.password);
          setShowVerificationModal(true);
          toast.success('Verification code sent to your email!');

          // Note: If user is 13-17, parental consent flow should happen after email verification
          // This will be handled in the verification success flow
        }
      } catch (err: any) {
        console.error('[ModernLogin] Registration failed:', err);

        // Clear password fields for security when registration fails
        if (err.code === 'UsernameExistsException' || err.message?.includes('already registered')) {
          setFormData(prev => ({
            ...prev,
            password: '',
            confirmPassword: ''
          }));
        }
      }
    }
  };

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (Is_Loading) return;

    console.log('[ModernLogin] Verifying code with username:', pendingUsername);
    try {
      await confirmRegistration(pendingUsername, verificationCode);
      console.log('[ModernLogin] Verification successful');

      toast.success('Email verified! Logging you in...');

      // Auto-login after verification
      await authenticate(pendingEmail, pendingPassword);

      // Send birth_date to backend registration endpoint after successful authentication
      const storedBirthDate = sessionStorage.getItem('user_birth_date');
      if (storedBirthDate) {
        console.log('[ModernLogin] Sending birth_date to backend:', storedBirthDate);
        try {
          await Make_Authorized_API_Call(API_ENDPOINTS.AUTH.REGISTER, 'POST', {
            email: pendingEmail,
            display_name: formData.name,
            user_type: 'parent',
            birth_date: storedBirthDate,
            age_verification: true,
            family_name: formData.familyName || `${formData.name}'s Family`
          });
          console.log('[ModernLogin] Backend registration with birth_date successful');
        } catch (regErr: any) {
          // If user already exists (created by Cognito post-confirmation trigger), that's expected
          // The birth_date will need to be updated via profile update
          console.warn('[ModernLogin] Backend registration call returned:', regErr.message || regErr);
        }

        // Clean up registration data from sessionStorage
        sessionStorage.removeItem('user_birth_date');
        sessionStorage.removeItem('user_age');
        console.log('[ModernLogin] Cleaned up registration sessionStorage data');
      }

      // Close modal
      setShowVerificationModal(false);
      setVerificationCode('');
      setPendingEmail('');
      setPendingUsername('');
      setPendingPassword('');
    } catch (err) {
      console.error('[ModernLogin] Verification failed:', err);
    }
  };

  const handleResendCode = async () => {
    if (Is_Loading) return;

    console.log('[ModernLogin] Resending verification code for username:', pendingUsername);
    try {
      await resendVerificationCode(pendingUsername);
      toast.success('Verification code resent to your email!');
    } catch (err) {
      console.error('[ModernLogin] Resend failed:', err);
      toast.error('Failed to resend code. Please try again.');
    }
  };

  const getPasswordStrength = (password: string): { strength: number; label: string; color: string } => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength <= 2) return { strength, label: 'Weak', color: 'bg-red-500' };
    if (strength <= 3) return { strength, label: 'Fair', color: 'bg-yellow-500' };
    if (strength <= 4) return { strength, label: 'Good', color: 'bg-blue-500' };
    return { strength, label: 'Strong', color: 'bg-green-500' };
  };

  const passwordStrength = authMode === 'signup' ? getPasswordStrength(formData.password) : null;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring" as const,
        stiffness: 300,
        damping: 24
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-honey-100 via-white to-purple-50 flex items-center justify-center p-4">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-honey rounded-full blur-3xl opacity-20 animate-float" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-purple rounded-full blur-3xl opacity-20 animate-float" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-blue rounded-full blur-3xl opacity-10 animate-pulse-slow" />
      </div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="relative z-10 w-full max-w-md"
      >
        <motion.div variants={itemVariants} className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-honey rounded-2xl mb-4 shadow-lg">
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <h1 className="text-4xl font-bold bg-gradient-honey bg-clip-text text-transparent mb-2">
              HoneyDo
            </h1>
            <p className="text-gray-600">Smart Home Task Management</p>
          </div>

          {/* Error Alert */}
          {Error_Message && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"
            >
              {Error_Message}
            </motion.div>
          )}

          {/* New Password Required (invited user with temp password) */}
          {needsNewPassword && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <h3 className="text-sm font-semibold text-amber-800">Welcome! Set your password</h3>
                <p className="text-sm text-amber-700 mt-1">
                  Please choose a permanent password for your account.
                </p>
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (newPassword !== confirmNewPassword) {
                  toast.error('Passwords do not match');
                  return;
                }
                if (newPassword.length < 8) {
                  toast.error('Password must be at least 8 characters');
                  return;
                }
                try {
                  await completeNewPassword(newPassword);
                  toast.success('Password set! Welcome to HoneyDo.');
                } catch (err: any) {
                  console.error('Set password failed:', err);
                }
              }} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Lock className="w-4 h-4 inline mr-2" />
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={Is_Loading}
                    placeholder="Choose a strong password"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-honey-400 focus:border-transparent transition-all duration-200 disabled:opacity-50"
                    required
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-1">Min 8 chars with uppercase, lowercase, number, and special character</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Shield className="w-4 h-4 inline mr-2" />
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    disabled={Is_Loading}
                    placeholder="Confirm your password"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-honey-400 focus:border-transparent transition-all duration-200 disabled:opacity-50"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={Is_Loading || !newPassword || newPassword !== confirmNewPassword}
                  className="w-full px-6 py-4 bg-gradient-honey text-white font-semibold rounded-xl hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {Is_Loading ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Setting password...</>
                  ) : (
                    <><CheckCircle2 className="w-5 h-5" /> Set Password & Continue</>
                  )}
                </button>
              </form>
            </motion.div>
          )}

          {/* Auth Mode Toggle - hidden during password change */}
          {!needsNewPassword && <div className="flex gap-2 p-1 bg-gray-100 rounded-xl mb-6">
            <button
              type="button"
              onClick={() => setAuthMode('signin')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all duration-200 ${
                authMode === 'signin'
                  ? 'bg-white text-honey-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => {
                if (onSignUpClick) {
                  // If age verification callback provided, use it
                  onSignUpClick();
                } else {
                  // Otherwise, just toggle to signup mode (backward compatible)
                  setAuthMode('signup');
                }
              }}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all duration-200 ${
                authMode === 'signup'
                  ? 'bg-white text-honey-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Sign Up
            </button>
          </div>}

          {/* Sign In / Sign Up Form - hidden during password change */}
          {!needsNewPassword && <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name Input (Sign Up Only) */}
            {authMode === 'signup' && (
              <motion.div
                variants={itemVariants}
                initial="hidden"
                animate="visible"
              >
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-2" />
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={Is_Loading}
                  placeholder="John Doe"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-honey-400 focus:border-transparent transition-all duration-200 disabled:opacity-50"
                  required
                />
              </motion.div>
            )}

            {/* Email Input */}
            <motion.div variants={itemVariants}>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Mail className="w-4 h-4 inline mr-2" />
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={Is_Loading}
                placeholder="your@email.com"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-honey-400 focus:border-transparent transition-all duration-200 disabled:opacity-50"
                required
              />
            </motion.div>

            {/* Password Input */}
            <motion.div variants={itemVariants}>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Lock className="w-4 h-4 inline mr-2" />
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  disabled={Is_Loading}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-honey-400 focus:border-transparent transition-all duration-200 disabled:opacity-50"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {/* Password Strength Indicator (Sign Up Only) */}
              {authMode === 'signup' && formData.password && passwordStrength && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-2"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">Password Strength</span>
                    <span className={`text-xs font-semibold ${
                      passwordStrength.strength <= 2 ? 'text-red-600' :
                      passwordStrength.strength <= 3 ? 'text-yellow-600' :
                      passwordStrength.strength <= 4 ? 'text-blue-600' :
                      'text-green-600'
                    }`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                      style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Must contain: uppercase, lowercase, number, special character (min 8 chars)
                  </p>
                </motion.div>
              )}
            </motion.div>

            {/* Confirm Password Input (Sign Up Only) */}
            {authMode === 'signup' && (
              <motion.div
                variants={itemVariants}
                initial="hidden"
                animate="visible"
              >
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Shield className="w-4 h-4 inline mr-2" />
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    disabled={Is_Loading}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-honey-400 focus:border-transparent transition-all duration-200 disabled:opacity-50"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {formData.confirmPassword && (
                  <div className="mt-1 flex items-center gap-1">
                    {formData.password === formData.confirmPassword ? (
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Passwords match
                      </span>
                    ) : (
                      <span className="text-xs text-red-600">Passwords do not match</span>
                    )}
                  </div>
                )}
              </motion.div>
            )}

            {/* Family Name Input (Sign Up Only - Optional) */}
            {authMode === 'signup' && (
              <motion.div
                variants={itemVariants}
                initial="hidden"
                animate="visible"
              >
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Users className="w-4 h-4 inline mr-2" />
                  Family Name (Optional)
                </label>
                <input
                  type="text"
                  name="familyName"
                  value={formData.familyName}
                  onChange={(e) => setFormData({ ...formData, familyName: e.target.value })}
                  disabled={Is_Loading}
                  placeholder="The Smiths"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-honey-400 focus:border-transparent transition-all duration-200 disabled:opacity-50"
                />
              </motion.div>
            )}

            {/* Terms of Service Agreement (Sign Up Only) */}
            {authMode === 'signup' && (
              <motion.div
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                className="flex items-start gap-3"
              >
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={(e) => setFormData({ ...formData, agreeToTerms: e.target.checked })}
                  disabled={Is_Loading}
                  required
                  className="mt-1 w-4 h-4 text-honey-600 border-gray-300 rounded focus:ring-2 focus:ring-honey-400 disabled:opacity-50"
                />
                <label htmlFor="agreeToTerms" className="text-sm text-gray-700">
                  I agree to the{' '}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-honey-600 hover:text-honey-700 font-medium underline"
                  >
                    Terms of Service
                  </a>
                  {' '}and{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-honey-600 hover:text-honey-700 font-medium underline"
                  >
                    Privacy Policy
                  </a>
                  {' '}(required for ages 13+)
                </label>
              </motion.div>
            )}

            {/* Submit Button */}
            <motion.div variants={itemVariants}>
              <button
                type="submit"
                disabled={Is_Loading}
                className="w-full px-6 py-4 bg-gradient-honey text-white font-semibold rounded-xl hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 group"
              >
                {Is_Loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {authMode === 'signin' ? 'Signing in...' : 'Creating account...'}
                  </>
                ) : (
                  <>
                    {authMode === 'signin' ? 'Sign In' : 'Create Account'}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </motion.div>

            {/* Family PIN Login Link */}
            {authMode === 'signin' && (
              <motion.div variants={itemVariants} className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/family-login')}
                  className="text-sm text-honey-600 hover:text-honey-700 font-medium transition-colors"
                >
                  Family Member? Use PIN →
                </button>
              </motion.div>
            )}
          </form>}
        </motion.div>
      </motion.div>

      {/* Email Verification Modal */}
      {showVerificationModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full"
          >
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-honey rounded-full mb-4">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Verify Your Email
              </h2>
              <p className="text-gray-600 text-sm">
                We sent a 6-digit verification code to
                <br />
                <span className="font-semibold text-gray-900">{pendingEmail}</span>
              </p>
            </div>

            {Error_Message && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {Error_Message}
              </div>
            )}

            <form onSubmit={handleVerification} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={Is_Loading}
                  placeholder="000000"
                  maxLength={6}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-center text-2xl font-mono tracking-wider focus:ring-2 focus:ring-honey-400 focus:border-transparent transition-all duration-200 disabled:opacity-50"
                  required
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Enter the 6-digit code from your email
                </p>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={Is_Loading}
                  className="text-sm text-honey-600 hover:text-honey-700 font-medium disabled:opacity-50"
                >
                  Didn't receive a code? Resend
                </button>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowVerificationModal(false);
                    setVerificationCode('');
                  }}
                  disabled={Is_Loading}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={Is_Loading || verificationCode.length !== 6}
                  className="flex-1 px-4 py-3 bg-gradient-honey text-white font-semibold rounded-xl hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {Is_Loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      Verify
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default ModernLogin;
